import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { getStatusChipToneStyles, StatusTone } from "theme/paletteUtils";

export type { StatusTone };

type ChipColors = { bg: string; fg: string; dot: string };

const TX_STATUS_COLORS: Record<string, { bg: string; fg: string }> = {
  new: { bg: "#d2dfea", fg: "#334155" },
  pending: { bg: "#bad7f2", fg: "#0d3b6e" },
  cancelled: { bg: "#bc4749", fg: "#fff" },
  declined: { bg: "#ee6055", fg: "#fff" },
  "declined by bank": { bg: "#ee6055", fg: "#fff" },
  review: { bg: "#00a1e4", fg: "#fff" },
  sending: { bg: "#064789", fg: "#fff" },
  sent: { bg: "#d9ed92", fg: "#3a5a0a" },
  confirmed: { bg: "#80ed99", fg: "#1a5c2a" },
  "sum sub rejected": { bg: "#ee6055", fg: "#fff" },
  "sumsub rejected": { bg: "#ee6055", fg: "#fff" },
  "pending verification": { bg: "#ffd97d", fg: "#7a5200" },
  "pending counterparty": { bg: "#f4a259", fg: "#6b3800" },
};

export const statusToColors = (raw?: string | null): ChipColors | null => {
  const exact = TX_STATUS_COLORS[(raw || "").toLowerCase()];
  if (!exact) return null;
  return {
    bg: exact.bg,
    fg: exact.fg,
    dot: exact.fg === "#fff" ? "#fff" : exact.fg,
  };
};

type Props = {
  label: string;
  tone?: StatusTone;
  status?: string | null;
  size?: "sm" | "md";
};

const StatusChip: React.FC<Props> = ({
  label,
  tone = "neutral",
  status,
  size = "sm",
}) => {
  const theme = useTheme();
  const exact = statusToColors(status);
  const { bg, fg, dot } = exact || getStatusChipToneStyles(theme, tone);
  const padX = size === "sm" ? 8 : 10;
  const padY = size === "sm" ? 3 : 5;

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        bgcolor: bg,
        color: fg,
        borderRadius: 999,
        px: `${padX}px`,
        py: `${padY}px`,
        whiteSpace: "nowrap",
        maxWidth: "100%",
      }}
    >
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          bgcolor: dot,
          flexShrink: 0,
        }}
      />
      <Typography
        component="span"
        variant={size === "sm" ? "statusLabel" : "statusLabelMd"}
      >
        {label}
      </Typography>
    </Box>
  );
};

export const statusToTone = (raw?: string | null): StatusTone => {
  if (statusToColors(raw)) {
    return "neutral";
  }
  const s = (raw || "").toLowerCase();
  if (s === "completed" || s === "approved" || s === "ready") return "success";
  if (s === "paid" || s === "sum sub on hold") return "info";
  if (s === "rejected" || s === "error" || s === "sum sub deleted")
    return "danger";
  if (
    s === "refunded" ||
    s === "chargeback" ||
    s === "replaced" ||
    s === "pending payment" ||
    s === "pending transfer" ||
    s === "start_verify" ||
    s === "confirm_waiting_documents" ||
    s === "sum sub reviewed"
  )
    return "warning";
  if (s === "sum sub awaiting user") return "neutral";
  return "neutral";
};

export default StatusChip;
